import React, { useEffect, useMemo, useState } from 'react';
import './leaderboard.css';

const TOP_N = 5;
const PUBLIC = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const TROPHY_IMG = `${PUBLIC}/img/trophy.png`;
const POINT_ICON = `${PUBLIC}/img/point-img.png`;
const TIME_ICON = `${PUBLIC}/img/timer-img.png`;
const MOBILE_LAYOUT_MQ = '(max-width: 768px)';

function podiumOrderIndices(n) {
  if (n <= 0) return [];
  if (n === 1) return [0];
  if (n === 2) return [1, 0];
  if (n === 3) return [1, 0, 2];
  if (n === 4) return [3, 1, 0, 2];
  return [3, 1, 0, 2, 4];
}

function parseTimeToSeconds(t) {
  if (t == null) return 0;
  if (typeof t === 'number' && Number.isFinite(t)) return Math.max(0, Math.floor(t));
  if (typeof t !== 'string') return 0;
  const s = String(t).trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10) || 0;
  const parts = s.split(':').map((p) => parseInt(String(p).trim(), 10) || 0);
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return 0;
}

function formatPodiumTime(raw) {
  const toMmSs = (sec) => {
    const t = Math.max(0, Math.floor(sec));
    const m = Math.floor(t / 60);
    const ss = t % 60;
    return `${m < 10 ? `0${m}` : m}:${ss < 10 ? `0${ss}` : ss}`;
  };

  if (raw == null || raw === '') return '00:00';
  if (typeof raw === 'number' && Number.isFinite(raw)) return toMmSs(raw);
  const s = String(raw).trim();
  if (/^\d+$/.test(s)) return toMmSs(parseInt(s, 10) || 0);
  return toMmSs(parseTimeToSeconds(s));
}

function pickDisplayScore(m) {
  const keys = ['points', 'total_score', 'spot_score', 'score'];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (!(key in m)) continue;
    const v = m[key];
    if (v == null || v === '') continue;
    const n = parseInt(String(v).trim(), 10);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  return 0;
}

function pickDisplayTime(m) {
  const raw = m.time ?? m.reportTime;
  if (raw == null || raw === '') return '00:00';
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(Math.max(0, Math.floor(raw)));
  const s = String(raw).trim().slice(0, 50);
  return s || '00:00';
}

function parsePositiveRank(v) {
  const n = parseInt(String(v ?? '').trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function normalizeLeaderboardMatch(m) {
  return { ...m, score: pickDisplayScore(m), time: pickDisplayTime(m) };
}

export function dedupeByUserIdBestScore(rows) {
  const map = new Map();
  for (const r of rows) {
    const id = r.userId != null && r.userId !== '' ? String(r.userId) : `k:${r.email || ''}:${r.name || ''}`;
    const prev = map.get(id);
    const score = r.score ?? 0;
    if (!prev || score > (prev.score ?? 0)) {
      map.set(id, r);
    } else if (prev && score === (prev.score ?? 0)) {
      const ta = parseTimeToSeconds(String(r.time));
      const tb = parseTimeToSeconds(String(prev.time));
      if (ta < tb) map.set(id, r);
    }
  }
  return [...map.values()];
}

function podiumHeightTier(displayRank) {
  if (displayRank === 1) return 1;
  if (displayRank === 2 || displayRank === 3) return 2;
  return 3;
}

function RankVisual({ rank }) {
  const trophySrc = TROPHY_IMG;
  const trophyAlt = rank === 1 ? 'Rank 1 trophy' : `Rank ${rank} trophy`;

  return (
    <div className={`lb-rank-visual${rank === 1 ? ' lb-rank-visual--champ' : ''}`} aria-hidden>
      <div className={`lb-rank-box${rank === 1 ? ' lb-rank-box--first' : ''}`}>
        <img src={trophySrc} alt={trophyAlt} className="lb-rank-box-img" />
        <span className="lb-rank-box-num">{rank}</span>
      </div>
    </div>
  );
}

function PodiumCard({ m, role }) {
  const tier = podiumHeightTier(m.displayRank);
  return (
    <div
      role={role}
      className={`lb-podium-card lb-podium-card--tier-${tier}${m.isSelf ? ' lb-podium-card--self' : ''}`}
    >
      <RankVisual rank={m.displayRank} />
      <div className="lb-podium-name">
        {m.name}
        {m.isSelf ? ' (You)' : ''}
      </div>
      <div className="lb-podium-score">
        <img src={POINT_ICON} alt="" className="lb-stat-icon" />
        <span>{m.score ?? 0}</span>
      </div>
      <div className="lb-podium-time">
        <img src={TIME_ICON} alt="" className="lb-stat-icon" />
        <span>{formatPodiumTime(m.time)}</span>
      </div>
    </div>
  );
}

function useIsMobileLayout() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_LAYOUT_MQ).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_LAYOUT_MQ);
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return isMobile;
}

/**
 * Shared podium UI for session leaderboard and per-category leaderboard.
 */
export default function LeaderboardLayout({
  title,
  matches,
  loading,
  fetchError,
  user,
  currentUserMeta,
  themeData,
  footer,
  emptyMessage = 'No data available yet.',
  errorHint = 'Check that the API is running and your session matches the game data.',
}) {
  const isMobileLayout = useIsMobileLayout();
  const headingColor = themeData?.text_color || '#4f3216';

  const sortedRows = useMemo(() => {
    const copy = [...matches];
    copy.sort((a, b) => {
      const sb = b.score ?? 0;
      const sa = a.score ?? 0;
      if (sb !== sa) return sb - sa;
      return parseTimeToSeconds(String(a.time)) - parseTimeToSeconds(String(b.time));
    });
    return copy.map((m, i) => ({ ...m, displayRank: i + 1 }));
  }, [matches]);

  const topFive = useMemo(() => sortedRows.slice(0, TOP_N), [sortedRows]);
  const currentUserRankRow = useMemo(() => {
    if (!sortedRows.length) return null;
    const uid = String(user?.userId || user?.id || '');
    if (!uid) return sortedRows.find((r) => r.isSelf) || null;
    return (
      sortedRows.find((r) => String(r.userId || '') === uid) ||
      sortedRows.find((r) => r.isSelf) ||
      null
    );
  }, [sortedRows, user]);
  const currentUserSummary = useMemo(() => {
    if (currentUserRankRow) {
      return {
        rank: currentUserRankRow.displayRank,
        score: currentUserRankRow.score ?? 0,
        time: currentUserRankRow.time,
        name: currentUserRankRow.name || 'You',
      };
    }
    if (currentUserMeta) {
      return {
        rank: currentUserMeta.rank ?? null,
        score: currentUserMeta.score ?? 0,
        time: currentUserMeta.time,
        name: currentUserMeta.name || 'You',
      };
    }
    return null;
  }, [currentUserRankRow, currentUserMeta]);
  const resolvedCurrentUserRank = useMemo(() => {
    const apiRank = parsePositiveRank(currentUserSummary?.rank);
    if (apiRank) return apiRank;
    if (!currentUserSummary) return null;
    const myId = String(user?.userId || user?.id || '');
    const meKey = myId || `self:${String(currentUserSummary.name || 'you').toLowerCase()}`;
    const poolMap = new Map();
    sortedRows.forEach((row) => {
      const key = row.userId != null && row.userId !== '' ? String(row.userId) : `n:${String(row.name || '').toLowerCase()}`;
      if (!poolMap.has(key)) poolMap.set(key, row);
    });
    if (!poolMap.has(meKey)) {
      poolMap.set(meKey, {
        userId: myId || null,
        name: currentUserSummary.name || 'You',
        score: currentUserSummary.score ?? 0,
        time: currentUserSummary.time ?? '00:00',
      });
    }
    const pool = Array.from(poolMap.values());
    pool.sort((a, b) => {
      const sb = b.score ?? 0;
      const sa = a.score ?? 0;
      if (sb !== sa) return sb - sa;
      return parseTimeToSeconds(String(a.time)) - parseTimeToSeconds(String(b.time));
    });
    const idx = pool.findIndex((row) => {
      if (myId && row.userId != null && String(row.userId) === myId) return true;
      if (!myId) {
        return String(row.name || '').trim().toLowerCase() === String(currentUserSummary.name || '').trim().toLowerCase();
      }
      return false;
    });
    return idx >= 0 ? idx + 1 : null;
  }, [currentUserSummary, sortedRows, user]);

  const podiumSlots = useMemo(() => {
    const n = topFive.length;
    const order = podiumOrderIndices(n);
    return order.map((idx) => topFive[idx]).filter(Boolean);
  }, [topFive]);
  const mobileSlots = useMemo(() => {
    const byRank = new Map(topFive.map((m) => [Number(m.displayRank), m]));
    const rowOne = [2, 1, 3].map((r) => byRank.get(r)).filter(Boolean);
    const rowTwo = [4, 5].map((r) => byRank.get(r)).filter(Boolean);
    if (rowOne.length || rowTwo.length) return { rowOne, rowTwo };
    return { rowOne: topFive.slice(0, 3), rowTwo: topFive.slice(3, 5) };
  }, [topFive]);

  return (
    <div className="lb-page lb-page--podium">
      <div className="lb-inner">
        <header className="lb-hero">
          <h1 className="lb-heading-main" style={{ color: headingColor }}>
            {title}
          </h1>
        </header>

        <div className="lb-podium-shell">
          {loading ? (
            <div className="lb-loading">
              <div className="lb-spinner" />
              <span>Loading rankings…</span>
            </div>
          ) : fetchError ? (
            <div className="lb-empty lb-empty--error">
              <p>{fetchError}</p>
              <p className="lb-empty-hint">{errorHint}</p>
            </div>
          ) : matches.length === 0 ? (
            <div className="lb-empty">
              <p>{emptyMessage}</p>
            </div>
          ) : isMobileLayout ? (
            <div className="lb-mob-podium" role="list" aria-label="Top five players">
              <div className="lb-mob-podium-row lb-mob-podium-row--top">
                {mobileSlots.rowOne.map((m) => (
                  <PodiumCard key={m.userId || `${m.name}-${m.displayRank}`} m={m} role="listitem" />
                ))}
              </div>
              <div className="lb-mob-podium-row lb-mob-podium-row--bottom">
                {mobileSlots.rowTwo.map((m) => (
                  <PodiumCard key={m.userId || `${m.name}-${m.displayRank}`} m={m} role="listitem" />
                ))}
              </div>
            </div>
          ) : (
            <div className="lb-podium-scroll">
              <div className="lb-podium" role="list" aria-label="Top five players">
                {podiumSlots.map((m) => (
                  <PodiumCard key={m.userId || `${m.name}-${m.displayRank}`} m={m} role="listitem" />
                ))}
              </div>
            </div>
          )}
        </div>
        {currentUserSummary ? (
          <div className="lb-current-user-rank" aria-live="polite">
            <span className="lb-current-user-rank__label">Your Result:</span>
            <span className="lb-current-user-rank__item">
              {' '}
              {currentUserSummary.name} (You) | Rank #{resolvedCurrentUserRank ?? '--'} | Points {currentUserSummary.score ?? 0} | Time{' '}
              {formatPodiumTime(currentUserSummary.time)}
            </span>
          </div>
        ) : null}

        {footer}
      </div>
    </div>
  );
}
